import {
  DetailsPanel,
  InputStatusIndicator,
  PointMap,
} from '@/components/controls';
import { dioStates, useDallasKeys } from '@/utils/config';
import { Navigation as NavigationIcon } from '@mui/icons-material';
import { Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { format } from 'date-fns';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { useState } from 'react';
import { useParams } from 'react-router';
import { ConfigurationPanel } from './ConfigurationPanel';
import { PollHistory } from './PollHistory';
import { SignalStrengthIcon } from './SignalStrengthIcon';
import { useTelematicsBoxPollStream } from './useTelematicsBoxPollStream';
import { useTelematicsBoxVehicle } from './useTelematicsBoxVehicle';
import {
  columns,
  convertSoftwareId,
  getBatteryColor,
  getCanBusStatus,
  getResetReason,
  getTrigger,
} from './utils';

export function TelematicsBoxTest() {
  const { id } = useParams();
  const [tab, setTab] = useState('stream');
  const [date, setDate] = useState(new Date());
  const {
    query: { data: vehicle },
  } = useTelematicsBoxVehicle(id);
  const polls = useTelematicsBoxPollStream(id);

  function handleTabChange(event, value) {
    setTab(value);
  }

  function handleDateChange(date) {
    setDate(date);
  }

  const table = useMaterialReactTable({
    data: polls,
    columns,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableSorting: false,
    defaultColumn: { size: 0 },
    initialState: {
      density: 'compact',
      sorting: [{ id: 'enqueuedTime', desc: true }],
    },
    muiTableBodyRowProps: {
      sx: { '&:last-child td, &:last-child th': { border: 0 } },
    },
  });

  return (
    <Stack spacing={1} sx={{ p: 1 }}>
      <Typography variant="h6" sx={{ p: 1 }}>
        {id} Test & Configure
      </Typography>
      <Stack spacing={1} direction="row" sx={{ flexWrap: 'wrap' }}>
        {polls[0]?.position && (
          <PointMap
            key={polls[0]?.time}
            point={polls[0].position}
            width={400}
            height={400}
          />
        )}
        <DetailsPanel
          entries={[
            {
              label: 'Recieved Time',
              value: polls[0]?.enqueuedTime
                ? format(polls[0].enqueuedTime, 'dd/MM/yyyy HH:mm:ss')
                : '',
            },
            {
              label: 'Ignition',
              value: <InputStatusIndicator value={polls[0]?.ignitionOn} />,
            },
            {
              label: 'Heading',
              value: (
                <Tooltip title={`${polls[0]?.headingDegrees ?? 0}º`} arrow>
                  <NavigationIcon
                    sx={{ fontSize: 16 }}
                    style={{
                      transform: `rotate(${polls[0]?.headingDegrees ?? 0}deg)`,
                    }}
                  />
                </Tooltip>
              ),
            },
            {
              label: 'Signal',
              value: (
                <Tooltip
                  title={polls[0]?.deviceProperties?.deviceSignalStrength}
                  arrow
                >
                  <SignalStrengthIcon
                    strength={polls[0]?.deviceProperties?.deviceSignalStrength}
                    sx={{ fontSize: 16 }}
                  />
                </Tooltip>
              ),
            },
            {
              label: 'Battery',
              value: (
                <Typography
                  noWrap
                  variant="caption"
                  color={getBatteryColor(
                    polls[0]?.deviceProperties?.batteryVoltage,
                  )}
                >
                  {polls[0]?.deviceProperties?.batteryVoltage
                    ? `${polls[0]?.deviceProperties?.batteryVoltage}v`
                    : 'N/A'}
                </Typography>
              ),
            },
            {
              label: 'Speed',
              value: `${Math.round((polls[0]?.speedKilometresPerHour ?? 0) * 0.62137119)} mph`,
            },
            {
              label: 'Device Odometer',
              value: `${Math.round((polls[0]?.distanceKilometres ?? 0) * 0.62137119)} mi`,
            },
            {
              label: 'Buffer Tally',
              value: polls[0]?.bufferCount ?? 0,
            },
            {
              label: 'CAN Bus',
              value: getCanBusStatus(polls[0]?.deviceProperties?.canBusStatus),
            },
            {
              label: 'Reset Reason',
              value: getResetReason(polls[0]?.deviceProperties?.resetReason),
            },
            {
              label: 'Trigger',
              value: getTrigger(
                +polls[0]?.diagnosticCode,
                polls[0]?.deviceProperties?.resetReason,
              ),
            },
            {
              label: useDallasKeys
                ? 'Dallas Key Serial #'
                : 'RFID Card Serial #',
              value: polls[0]?.deviceProperties?.driverId ?? '-',
            },
            {
              label: 'Firmware Version',
              value: convertSoftwareId(polls[0]?.deviceProperties?.softwareId),
            },
          ]}
        />
        <Stack spacing={1}>
          <DetailsPanel
            title="GPS"
            entries={[
              {
                label: 'Fix',
                value: (
                  <InputStatusIndicator
                    value={polls[0]?.deviceProperties?.isValidated}
                    offColor="error.main"
                    onText="Yes"
                    offText="No"
                  />
                ),
              },
              {
                label: 'Time',
                value: polls[0]?.time
                  ? format(polls[0].time, 'dd/MM/yyyy HH:mm:ss')
                  : '-',
              },
              {
                label: 'Satellites',
                value: polls[0]?.deviceProperties?.satelliteCount ?? 0,
              },
            ]}
          />
          <DetailsPanel
            title="Inputs"
            entries={Object.entries(dioStates).map(([key, label]) => ({
              label,
              value: <InputStatusIndicator value={polls[0]?.[key]} />,
            }))}
          />
        </Stack>
        <Stack spacing={1} sx={{ flexGrow: 1 }}>
          <DetailsPanel
            title="Vehicle"
            entries={[
              {
                label: 'VIN',
                value: vehicle?.identificationNumber ?? '-',
              },
              {
                label: 'Registration',
                value: vehicle?.registrationNumber ?? '-',
              },
              {
                label: 'Fleet #',
                value: vehicle?.fleetNumber ?? '-',
              },
              {
                label: 'Type',
                value: [vehicle?.make, vehicle?.model].join(' ') ?? '-',
              },
              {
                label: 'Role',
                value: vehicle?.role ?? '-',
              },
            ]}
          />
          <ConfigurationPanel imei={id} />
        </Stack>
      </Stack>
      <Tabs value={tab} onChange={handleTabChange} centered>
        <Tab label="Poll Stream" value="stream" />
        <Tab label="Poll History" value="history" />
      </Tabs>
      {tab === 'stream' ? (
        <MaterialReactTable table={table} />
      ) : (
        <PollHistory imei={id} date={date} onDateChange={handleDateChange} />
      )}
    </Stack>
  );
}
